import { RegistrationScreen } from '@/components/utils/RegistrationScreen';
import { formatDayJSDate } from '@/components/utils/utils';
import * as P from '@/constants/paths';
import { toast } from '@/hooks/use-toast';
import useRegisterStore, {
  RegistrationTrack,
} from '@/hooks/useRegistrationStore';
import { deSanitizedKitId } from '@/hooks/registration/useRegistrationKitId';
import {
  useConfirmDetailsMutation,
  useUserConsentMutation,
} from '@viome/viome-base/src/usecase/Register/RegisterQuery';
import { useNavigate } from 'react-router-dom';
import { InfoSection } from './InfoSection';
import {
  isMinorConfirmConsent,
  isOptionSelectConsent,
} from '@/utils/consentUtils';
import { useUserStore } from '@/hooks/useUserStore';

export const RegistrationConfirmation = () => {
  const {
    kitId,
    email,
    firstName,
    lastName,
    phone,
    phoneCode,
    dob,
    street,
    country,
    city,
    apartment,
    zip,
    gender,
    shippable,
    state,
    setRestrictedRegistrationSteps,
    setConsents,
  } = useRegisterStore();

  const navigate = useNavigate();
  const { user: userData } = useUserStore();

  const {
    isPending: isPendingConfirmation,
    error: errorConfirmation,
    mutate: mutateConfirmation,
  } = useConfirmDetailsMutation();

  const {
    isPending: isPendingConsent,
    error: errorConsent,
    mutate: mutateConsent,
  } = useUserConsentMutation();

  // Prefill ISO is UTC midnight — format in UTC so the day does not shift
  const dobReadableFormat = dob
    ? new Date(dob).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        ...(/^\d{4}-\d{2}-\d{2}$/.test(dob) ||
        dob.endsWith('T00:00:00.000Z')
          ? { timeZone: 'UTC' }
          : {}),
      })
    : '';
  const formattedAddress = `${street}, ${apartment}
  ${city}, ${state} ${zip}
  ${country}`;

  const loading = isPendingConfirmation || isPendingConsent;

  const callConsent = () => {
    mutateConsent(
      {
        countryCode: country,
        dateOfBirth: formatDayJSDate(dob),
        kitId: deSanitizedKitId(kitId),
      },
      {
        onSuccess: (data) => {
          const augmentedConsents = data.map((consent) => ({
            ...consent,
            isMinor: isMinorConfirmConsent(consent),
            isWithChoice: isOptionSelectConsent(consent),
          }));
          setConsents(augmentedConsents);
          Promise.all([
            setRestrictedRegistrationSteps([]),
            navigate(P.REGISTRATION_USER_CONSENT),
          ]);
        },
        onError: () => {
          toast({ variant: 'destructive', description: errorConsent?.message });
        },
      },
    );
  };

  const onConfirm = () => {
    const apiData = {
      kitId: deSanitizedKitId(kitId),
      address: {
        addressLine1: street,
        addressLine2: apartment,
        city,
        country,
        state,
        zip,
        isShippable: shippable,
      },
      dateOfBirth: formatDayJSDate(dob),
    };
    mutateConfirmation(apiData, {
      onSuccess: (data) => {
        if (data?.registrationAllowed) {
          callConsent();
        }
      },
      onError: () => {
        toast({ variant: 'destructive', description: errorConfirmation?.message });
      },
    });
  };

  return (
    <RegistrationScreen
      title="Confirm Your Details"
      description="Take a look at your information below to ensure it's correct. We may
          contact you using below information."
      content={
        <div data-testid="registrationConfirmation-content">
          <InfoSection
            className="mt-3"
            title="Registration Information"
            editPath={P.REGISTRATION_KIT_ID}
            items={[{ field: 'Kit ID', value: kitId }]}
            data-testid="registrationConfirmation-registrationInfo"
          />

          <InfoSection
            className="mt-8"
            title="Account information"
            editPath={userData ? P.REGISTRATION_CONTACT_INFORMATION : P.REGISTRATION_PERSONAL_INFORMATION}
            items={[
              ...(!userData ? [
                { field: 'Email ID', value: email }, 
                { field: 'Name', value: `${firstName} ${lastName}` },
                { field: 'Birthday', value: dobReadableFormat },
                { field: 'Biological Sex', value: gender },] : []),
              { field: 'Address of Residency', value: formattedAddress },
              {
                field: 'Phone Number',
                value: `${phoneCode.countryPhoneCode} (${phoneCode.countryCode}) ${phone}`,
              },
            ]}
            data-testid="registrationConfirmation-accountInfo"
          />
        </div>
      }
      primaryActionLabel="My information is correct"
      primaryActionOnClick={onConfirm}
      isLoading={loading}
      checkForRegisterStore={RegistrationTrack.RegistrationConfirmation}
      data-testid="registrationConfirmation-screen"
    />
  );
};
